import React, { ChangeEvent, Fragment, useMemo } from "react";

import { Box, Button, Divider, FormLabel, IconButton, Radio, RadioGroup, Typography } from "@material-ui/core";
import clsx from "clsx";

import { useTranslation } from "react-i18next";

import BusinessOrderType from "api/types/BusinessOrderType";
import { AvailableOrderTypes } from "api/types/enums";

import PlacesAutocomplete from "components/PlacesAutocomplete";
import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import CloseCircle from "icons/CloseCircle";
import OrderIcon from "icons/OrderIcon";

import SelectPickUpDateTime from "./SelectPickUpDateTime";

const useStyles = makeStyles(theme => ({
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start",
		[theme.breakpoints.up(480)]: {
			display: "none"
		}
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff",
		fill: "#fff"
	},
	body: {
		[theme.breakpoints.up(480)]: {
			height: "auto",
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	titleRow: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		alignItems: "center"
	},
	icon: {
		verticalAlign: "center",
		marginRight: 10,
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: 16,
		letterSpacing: "0.38px"
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	itemDivider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
		backgroundColor: "#f2f2f2"
	},
	item: {
		marginLeft: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		fontWeight: 500,
		letterSpacing: "0.38px",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.secondary
		}
	},
	itemTitleChecked: {
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.primary
		}
	},
	radio: {
		color: "#f2f2f2",
		"&$checked": {
			color: theme.palette.primary.main
		},
		borderColor: "#fff"
	},
	checked: {
		marginRight: "-2px"
	},
	textField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(3),
		borderColor: "#fff",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8
	},
	radioIcon: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #f2f2f2",
		fill: theme.palette.text.primary,
		[theme.breakpoints.up(480)]: {
			borderColor: theme.palette.text.primary,
			width: "18px",
			height: "18px",
			margin: "3px"
		}
	},
	radioIconChecked: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #fff",
		color: theme.palette.primary.main,
		backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
		checked: {
			color: theme.palette.primary.main
		},
		"&:before": {
			display: "block",
			width: 11,
			height: 11,
			backgroundImage: "radial-gradient(#BDBDBD,#BDBDBD 56%,transparent 32%)",
			content: '""'
		}
	},
	addButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "100%",
		padding: 0,
		alignContent: "center",
		justifyContent: "center",
		textTransform: "uppercase"
		// "&:hover": {
		// 	[theme.breakpoints.up(480)]: {
		// 		backgroundColor: theme.palette.primary.main,
		// 		color: theme.palette.text.primary
		// 	}
		// }
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		padding: 0,
		position: "absolute",
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			width: "auto",
			flexDirection: "column",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	buttonIcon: {
		position: "absolute",
		right: 10
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	}
}));

interface Props {
	onClose: () => void;
}

const OrderTypeModal = ({ onClose }: Props) => {
	const { business } = useBusiness();
	const { orderType, phoneNumber, email, fullName, deliveryAddress, setOrderType, setCustomerInfo } = useCart();
	const classes = useStyles();
	const { t } = useTranslation();

	const value = orderType?.id;

	const handleChange = useMemo(
		() => (event: ChangeEvent<HTMLInputElement> | null, value: string) => {
			const orderType = business.orderTypes.find(({ id }) => id === value);
			setOrderType(orderType || ({} as BusinessOrderType));
		},
		[business.orderTypes, setOrderType]
	);

	return (
		<>
			<IconButton className={classes.closeButton} onClick={onClose}>
				<CloseCircle className={classes.closeIcon} />
			</IconButton>
			<Box className={classes.body}>
				<Box className={classes.titleRow}>
					<OrderIcon className={classes.icon} />
					<Typography className={classes.title}>{t("orderType")}</Typography>
				</Box>

				<RadioGroup className={classes.items} aria-label="option" value={value} onChange={handleChange}>
					{business.orderTypes.map(({ id, name }) => (
						<Fragment key={id}>
							<FormLabel className={classes.item}>
								<Typography className={clsx(classes.itemTitle, { [classes.itemTitleChecked]: value === id })}>
									{name}
								</Typography>
								<Radio
									inputProps={{ "aria-label": name }}
									icon={<span className={classes.radioIcon} />}
									classes={{ root: classes.radio, checked: classes.checked }}
									color="secondary"
									value={id}
								/>
							</FormLabel>
						</Fragment>
					))}
				</RadioGroup>

				<Divider className={classes.itemDivider} />

				{orderType?.orderType !== AvailableOrderTypes.pickup ? (
					<PlacesAutocomplete
						className={classes.textField}
						placeholder={t("deliveryAddress")}
						defaultValue={deliveryAddress?.place}
						origin={business?.address?.coordinates}
						radius={orderType?.deliveryRadiusMeters || undefined}
						onChange={(event, value) => {
							setCustomerInfo({
								phoneNumber,
								email,
								fullName,
								deliveryAddress: value && {
									address1: value.structured_formatting.main_text,
									address2: null,
									address3: null,
									city: value.structured_formatting.secondary_text,
									state: null,
									postalCode: null,
									country: null,
									placeId: value.place_id,
									place: value
								}
							});
						}}
					/>
				) : null}

				<SelectPickUpDateTime isEditable={true} />
			</Box>
			<DesktopContainerDrawer>
				<Box className={classes.footer}>
					<Button variant="outlined" className={classes.addButton} onClick={onClose}>
						<CloseCircle className={classes.closeIcon} />
						<Typography className={classes.actionButtonLabel}>{t("close")}</Typography>
					</Button>
				</Box>
			</DesktopContainerDrawer>
		</>
	);
};

export default OrderTypeModal;
